// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';

// Http Context
import { BYPASS } from 'app/auth/helpers';

// Environment
import { environment } from 'environments/environment';

// Extensions
import { ToastrService } from 'ngx-toastr';

// Models
import { _User as models, Role } from 'app/auth/models/index';

// RxJS
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';

// Services
import * as service from 'app/center/service/index';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  // Public
  public currentUser: Observable<models.User>;

  // Private
  private currentUserSubject: BehaviorSubject<models.User>;

  constructor(private _http: HttpClient, private _toastrService: ToastrService, private _centerService: service.Center) {
    this.currentUserSubject = new BehaviorSubject<models.User>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
    if (environment.production) {
      localStorage.removeItem('currentUser');
    }
  }

  // getter: currentUserValue
  public get currentUserValue(): models.User {
    return this.currentUserSubject.value;
  }

  // getter: auth headers
  public get authHeaders(): models.AuthHeaders {
    const user = this.currentUserSubject.value;
    return {
      iduser: user.id,
      key: user.password,
    };
  }

  get isOnline(): boolean {
    return this.currentUser && this.currentUserSubject.value !== null;
  }

  get isDeveloper(): boolean {
    return this.currentUser && this.currentUserSubject.value.role === Role.Developer;
  }

  get isManager(): boolean {
    return this.currentUser && this.currentUserSubject.value.role === Role.Manager;
  }

  get isCoordinator(): boolean {
    return this.currentUser && this.currentUserSubject.value.role === Role.Coordinator;
  }

  get isEmployee(): boolean {
    return this.currentUser && this.currentUserSubject.value.role === Role.Employee;
  }

  get isClient(): boolean {
    return this.currentUser && this.currentUserSubject.value.role === Role.Client;
  }

  get role(): Role {
    return this.currentUserSubject.value.role;
  }

  login(idUser: string, key: string): Observable<models.User> {
    return this._http
      .get<models.UserResponse>(`${environment.apiUrl}/api-vlight/login`, {
        observe: 'response',
        context: new HttpContext().set(BYPASS, true),
        headers: new HttpHeaders().set('key', key).set('iduser', idUser),
      })
      .pipe(
        map((httpResponse) => httpResponse.body),
        map((user) => {
          const userInfo: models.User = {
            id: user._id,
            email: user.email,
            password: key,
            fullName: user.nombreCompleto,
            role: user.rol,
          };

          if (!environment.production) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(userInfo));
          }

          // Display welcome toast!
          setTimeout(() => {
            this._toastrService.success(
              'Ha iniciado la sesión con éxito como ' +
                userInfo.role +
                ' en Journey Access App. Ahora puedes empezar a explorar. ¡Que lo disfrutes! 🎉',
              '!👋 Bienvenido, ' + userInfo.fullName + '!',
              { toastClass: 'toast ngx-toastr', closeButton: true }
            );
          }, 1500);

          // notify
          this.currentUserSubject.next(userInfo);
          return userInfo;
        })
      );
  }

  logout(): void {
    // remove current center
    this._centerService.cleanCurrentCenter();
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    // notify
    this.currentUserSubject.next(null);
  }

  downloadAccessFile(idUser: string, password: string): Observable<any> {
    return this._http
      .get<any>(`${environment.apiUrl}/api-vlight/key-access`, {
        observe: 'response',
        context: new HttpContext().set(BYPASS, true),
        params: new HttpParams().set('_id', idUser).set('password', password),
      })
      .pipe(
        map((httpResponse) => httpResponse.body),
        map((file) => {
          // Display welcome toast!
          this._toastrService.success(
            'Se genero con exito tu archivo de acceso. Ahora puedes empezar a explorar. ¡Que lo disfrutes! 🎉',
            '¡👋 Bienvenido!',
            { toastClass: 'toast ngx-toastr', closeButton: true }
          );

          return file;
        })
      );
  }
}
